import { AxiosError } from 'axios';
import FeatherIcon from 'feather-icons-react';
import { FormEventHandler, useState } from 'react';
import { Col, Form, InputGroup, Row, Toast, ToastContainer } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import SubmitButton from '../../../components/Buttons/SubmitButton';
import { DEFAULT_ERROR_MESSAGE } from '../../../consts';
import { isEmptyPassword } from '../../../helpers/validation';
import { updateToken } from '../../../store/redux/userRedux';
import { authApi } from '../../../utils/api';
import { TOKEN_KEY } from '../../../utils/tenant';

interface ChangePasswordResponse {
  token: string;
}

const ChangePassword = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [oldPasswordShown, setOldPasswordShown] = useState(false);
  const [newPasswordShown, setNewPasswordShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const toggleCurrentPasswordVisibility = () => {
    setOldPasswordShown(!oldPasswordShown);
  };
  const toggleNewPasswordVisibility = () => {
    setNewPasswordShown(!newPasswordShown);
  };

  const changePasswordHandler: FormEventHandler = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await authApi<ChangePasswordResponse>({
        method: 'put',
        url: '/auth/change-password',
        data: {
          oldPassword,
          newPassword
        }
      });

      setLoading(false);
      dispatch(updateToken(data.token));
      localStorage.setItem(TOKEN_KEY, data.token);
      navigate('/');
    } catch (error) {
      setLoading(false);
      error instanceof AxiosError ? setError(error.response?.data?.message) : setError(DEFAULT_ERROR_MESSAGE);
      setTimeout(() => {
        setError('');
      }, 3000);
    }
  };

  return (
    <>
      <h1 className="display-6 text-center mb-3">Change Password</h1>
      <p className="text-muted text-center mb-5">Need a stronger security?</p>
      <form onSubmit={changePasswordHandler}>
        <div className="form-group">
          <Row>
            <Col>
              <Form.Label htmlFor="current-password">Current Password</Form.Label>
            </Col>
          </Row>
          <InputGroup className="input-group-merge">
            <Form.Control
              type={`${oldPasswordShown ? 'text' : 'password'}`}
              id="current-password"
              placeholder="Enter current password"
              value={oldPassword}
              onChange={e => setOldPassword(e.target.value)}
              isValid={!isEmptyPassword(oldPassword)}
            />
            <InputGroup.Text onClick={toggleCurrentPasswordVisibility}>
              <FeatherIcon icon={`${oldPasswordShown ? 'eye-off' : 'eye'}`} size="1em" />
            </InputGroup.Text>
          </InputGroup>
        </div>

        <div className="form-group">
          <Row>
            <Col>
              <Form.Label htmlFor="new-password">New Password</Form.Label>
            </Col>
          </Row>
          <InputGroup className="input-group-merge">
            <Form.Control
              type={`${newPasswordShown ? 'text' : 'password'}`}
              id="new-password"
              placeholder="Enter New password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              isValid={!isEmptyPassword(newPassword)}
            />
            <InputGroup.Text onClick={toggleNewPasswordVisibility}>
              <FeatherIcon icon={`${newPasswordShown ? 'eye-off' : 'eye'}`} size="1em" />
            </InputGroup.Text>
          </InputGroup>
        </div>

        <SubmitButton
          disabled={isEmptyPassword(oldPassword) || isEmptyPassword(newPassword)}
          loading={loading}
          buttonText="Change password"
          type="submit"
        ></SubmitButton>

        {error && (
          <ToastContainer position="bottom-center" className="p-3">
            <Toast bg="danger">
              <Toast.Body className="text-white">{error}</Toast.Body>
            </Toast>
          </ToastContainer>
        )}
      </form>
    </>
  );
};

export default ChangePassword;
